import React, { useState, useEffect, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useNavigate, useParams } from 'react-router-dom'
import { Projet } from '../../types/projetTypes'
import { getProjetbyId, updateProjet } from '../../redux/projets/projetActions'
import UseAlert from '../../components/common/alert'
import SelectPriority from '../../components/common/SelectPriority'
import { useTranslation } from 'react-i18next'
import DateSelector from '../../components/common/Date'
import SelectStatut from '../../components/common/SelectStatut'
import SelectTypeProjet from '../../components/common/SelectTypeProjet'
import SelectPipeline from '../../components/common/SelectPipeline'
import { MissionSummaryCard } from '../../components/quickCreate/ParentSummaryCard'
import { useMissionBudget } from '../../components/quickCreate/parentInfo'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'
import '../../i18n'
import NumberInput from '../../components/common/NumberInput'

export default function ProjetUpdate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()
  const { id } = useParams<{ id: string }>()

  const [priority, setPriority] = useState<number>(1)
  const [status, setStatus] = useState<string>('EnCours')
  const [TypeProjet, setTypeProjet] = useState<string>('')
  const [DateDebut, setDateDebut] = useState<Date>(new Date())
  const [DateFin, setDateFin] = useState<Date>(new Date())
  const [projet, setProjet] = useState<Projet>({
    _id: '',
    ProjetName: '',
    Description: '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: '',
    Priority: 0,
    Marge: 0,
    Budget: 0,
    TypeProjet: '',
    Source: '',
    MissionId: '',
    PipelineId: '',
  })

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const projetsList = useSelector((state: RootState) => state.projet.projet)
  const loading = useSelector((state: RootState) => state.projet.loading)
  const error = useSelector((state: RootState) => state.projet.error)

  useEffect(() => {
    if (id) dispatch(getProjetbyId(id))
  }, [dispatch, id])

  useEffect(() => {
    if (projetsList !== null) {
      setProjet({
        _id: projetsList._id,
        ProjetName: projetsList.ProjetName,
        Description: projetsList.Description,
        DateDebut: projetsList.DateDebut,
        DateFin: projetsList.DateFin,
        Status: projetsList.Status,
        Priority: projetsList.Priority,
        Marge: projetsList.Marge,
        Budget: projetsList.Budget,
        TypeProjet: projetsList.TypeProjet,
        Source: projetsList.Source,
        MissionId: projetsList.MissionId,
        PipelineId: projetsList.PipelineId,
        PipelineName: projetsList.PipelineName,
      })
      setPriority(projetsList.Priority || 1)
      setStatus(projetsList.Status || '')
      setTypeProjet(projetsList.TypeProjet || '')
      setDateDebut(
        projetsList.DateDebut ? new Date(projetsList.DateDebut) : new Date()
      )
      setDateFin(
        projetsList.DateFin ? new Date(projetsList.DateFin) : new Date()
      )
    }
  }, [projetsList])

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
    navigate('/projets')
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setProjet((prev) => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (dates: [Date, Date] | null) => {
    if (!dates) return
    const [start, end] = dates
    setDateDebut(start)
    setDateFin(end)
    setProjet((prev) => ({
      ...prev,
      DateDebut: start,
      DateFin: end,
    }))
  }

  const createStringHandler =
    (
      setStringState: React.Dispatch<React.SetStateAction<string>>,
      property: string
    ) =>
    (event: React.ChangeEvent<{ value: string }>) => {
      const { value } = event.target
      setStringState(value)
      setProjet((prev) => ({ ...prev, [property]: value }))
    }

  const createNumberHandler =
    (
      setNumberState: React.Dispatch<React.SetStateAction<number>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: number }>) => {
      const { value } = event.target
      setNumberState(value)
      setProjet((prev) => ({ ...prev, [property]: value }))
    }

  const handlePriorityChange = createNumberHandler(setPriority, 'Priority') as (
    event: React.ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void
  const handleTypeProjet = createStringHandler(setTypeProjet, 'TypeProjet') as (
    event: SelectChangeEvent<string>
  ) => void
  const handleStatusChange = createStringHandler(setStatus, 'Status') as (
    event: SelectChangeEvent<string>
  ) => void
  const handlePipelineChange = (event: SelectChangeEvent<string>) => {
    setProjet((prev) => ({ ...prev, PipelineId: event.target.value }))
  }

  const submitLabel = t('Update')

  const missionBudget = useMissionBudget(projet.MissionId || undefined)
  const fmt = useNumberFormat()
  const savedBudget = Number(projetsList?.Budget) || 0

  const handleUpdateProjet = async () => {
    if (
      missionBudget.data &&
      Number(projet.Budget) > missionBudget.data.remaining + savedBudget
    ) {
      setAlert({
        open: true,
        message: t('parentLimits.budgetOver', {
          available: fmt.money(missionBudget.data.remaining + savedBudget),
        }),
        severity: 'error',
      })
      return
    }
    try {
      if (projet._id) {
        await dispatch(
          updateProjet({
            updatedProjet: projet,
            _id: projet._id,
          })
        ).unwrap()
        setAlert({
          open: true,
          message: t('alerts.projectUpdate.success'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.projectUpdate.emptyFields'),
          severity: 'error',
        })
      }
    } catch (err: any) {
      setAlert({
        open: true,
        message: err || t('alerts.projectUpdate.error'),
        severity: 'error',
      })
    }
  }

  if (loading && !projet._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress sx={{ color: '#0d9b87' }} />
      </Box>
    )
  }

  if (error && !projet._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.project.updateEyebrow', {
          defaultValue: 'Management · Projects',
        })}
        title={t('Update project')}
        subtitle={t('entityForms.project.updateSubtitle', {
          defaultValue:
            'Update project identity, planning, budget and pipeline linkage.',
        })}
        backLabel={t('entityForms.project.backToList', {
          defaultValue: 'Back to projects',
        })}
        onBack={() => navigate('/projets')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.project.identityTitle', {
                defaultValue: 'Identification',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Project Name')} required span>
                  <input
                    className="incyia-form-inp"
                    name="ProjetName"
                    value={projet.ProjetName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={projet.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
                <FormField label={t('Source')} required span>
                  <input
                    className="incyia-form-inp"
                    name="Source"
                    value={projet.Source}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.project.planningTitle', {
                defaultValue: 'Planning and cost',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Status')}>
                  <SelectStatut
                    value={projet.Status || ''}
                    onChange={handleStatusChange}
                    hideLabel
                  />
                </FormField>
                <FormField label={t('Priority')}>
                  <SelectPriority
                    value={projet.Priority || 0}
                    onChange={handlePriorityChange}
                  />
                </FormField>
                <FormField label={t('Budget')} required>
                  <NumberInput
                    currency
                    min={0}
                    name="Budget"
                    value={projet.Budget}
                    onChange={(v) => setProjet((prev) => ({ ...prev, Budget: v ?? 0 }))}
                    required
                  />
                </FormField>
                <FormField label={t('Project Type')}>
                  <SelectTypeProjet
                    value={projet.TypeProjet || ''}
                    onChange={handleTypeProjet}
                  />
                </FormField>
                <FormField
                  label={t('Pipeline')}
                  span
                >
                  <SelectPipeline
                    value={projet.PipelineId || ''}
                    onChange={handlePipelineChange}
                    MissionId={projet.MissionId || ''}
                  />
                </FormField>
                <FormField
                  label={t('entityForms.project.dates', {
                    defaultValue: 'Start & end dates',
                  })}
                  span
                  help={
                    projet.MissionId ? t('parentLimits.missionNoDates') : undefined
                  }
                >
                  {/* TODO(backend): Mission has no DateDebut/DateFin, so project dates cannot be bounded by the mission. */}
                  <DateSelector
                    placeholder={t('entityForms.datePlaceholder')}
                    onDateChange={handleDateChange}
                    defaultValue={[DateDebut, DateFin]}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate('/projets')}
              onSubmit={handleUpdateProjet}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.project.tip', {
              defaultValue:
                'Pipeline and dates drive the Gantt view on the project detail page.',
            })}
            context={
              projet.MissionId ? (
                <>
                  <MissionSummaryCard
                    plain
                    info={missionBudget.data}
                    status={missionBudget.status}
                  />
                  <p className="incyia-aside-note incyia-aside-divider">
                    {t('parentLimits.stayInside')}
                  </p>
                </>
              ) : undefined
            }
            steps={[
              {
                title: t('entityForms.project.identityTitle', {
                  defaultValue: 'Identification',
                }),
                fieldCount: 3,
              },
              {
                title: t('entityForms.project.planningTitle', {
                  defaultValue: 'Planning and cost',
                }),
                fieldCount: 6,
              },
            ]}
            requiredCount={4}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
    </div>
  )
}
